/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IImageListViewProps } from '@msdyn365-commerce-modules/image-list';
import { fixImageSettings } from '../../../shared-utils/image-utilities';
import { IImageListConfig } from '../definition-extensions/image-list.ext.props.autogenerated';

/**
 * Render the thumbnail item images.
 * @param props - The image list view props.
 * @returns Return HTML having IImageListViewProps props with image List.
 */
export const imageListView = (props: IImageListViewProps): JSX.Element => {
    const {
        imageList,
        imageListContainer,
        imageListHeading,
        imageListItem,
        imageListItems,
        imageListItemAnchorTag
    } = props;
    const config = props.config as IImageListConfig;

    return (
        <Module {...imageList}>
            {imageListHeading}
            <Node {...imageListContainer}>
                {imageListItems?.map(item => {
                    let indexKey: string;
                    let fixedImage: JSX.Element | undefined = item.image;

                    if (fixedImage) {
                        const imageSettings: Msdyn365.IImageSettings = fixedImage.props.imageSettings;

                        if (config.disableRemoteImageHandling) {
                            fixImageSettings(imageSettings, true, 'png');
                        }

                        fixedImage = React.cloneElement(fixedImage, { imageSettings });

                        const image = (fixedImage as unknown) as Msdyn365.Image;
                        indexKey = image.props.src as string;
                    } else {
                        const text = (item.text as unknown) as Msdyn365.RichTextComponent;
                        indexKey = text.props.text as string;
                    }
                    return (
                        <Node {...imageListItem} key={indexKey}>
                            {fixedImage && (
                                <Node
                                    {...imageListItemAnchorTag}
                                    href={item.link}
                                    altText={item.linkAltText}
                                    aria-label={item.linkAriaLabel}
                                    className={imageListItemAnchorTag.className}
                                    target={item.linkOpenInNewTab ? '_blank' : undefined}
                                >
                                    {fixedImage}
                                </Node>
                            )}
                            {item.text}
                        </Node>
                    );
                })}
            </Node>
        </Module>
    );
};

export default imageListView;
